// CoverageVerifying — overlay full-screen mostrato durante l'attesa della verifica
// copertura: anima l'interrogazione della rete (radar + step sequenziali + barra) e
// fa da "minimo garantito" mentre la fetch reale gira in parallelo, così l'overlay
// non lampeggia su risposte velocissime. Look pixel-perfect dal design handoff
// (design_handoff_coverage_overlay, hifi). Stili .cvf-* in styles/design.css.

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Icons } from './Icons'

interface VerifyStep {
  label: string
  sub: string
}

const STEPS: VerifyStep[] = [
  { label: 'Connessione ai sistemi Spadhausen', sub: 'handshake sicuro · TLS' },
  { label: 'Interrogazione centrali in fibra', sub: 'verifica nodo FTTH / FTTC' },
  { label: 'Controllo copertura radio FWA', sub: 'ponte verso la torre più vicina' },
  { label: 'Calcolo velocità stimata', sub: "in base all'infrastruttura in zona" },
]

const STEP_MS = 620

// Durata minima di permanenza dell'overlay (≈ 3.36s): il chiamante la usa per fare
// gating sull'apply (Promise.all con la fetch reale), così l'animazione completa.
export const COVERAGE_VERIFY_MIN_MS = STEP_MS * (STEPS.length + 1) + 260

interface Props {
  // Indirizzo in verifica. Se null l'overlay non viene renderizzato.
  target: { address: string } | null
}

export function CoverageVerifying({ target }: Props) {
  const [step, setStep] = useState(0)

  useEffect(() => {
    if (!target) return
    setStep(0)
    const timers = STEPS.map((_, i) => setTimeout(() => setStep(i + 1), STEP_MS * (i + 1)))
    // Blocco lo scroll del body mentre l'overlay è aperto.
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      timers.forEach(clearTimeout)
      document.body.style.overflow = prevOverflow
    }
  }, [target])

  if (!target) return null

  return createPortal(
    <div className="cvf-overlay" role="dialog" aria-live="polite" aria-label="Verifica copertura in corso">
      <div className="cvf-card">
        <div className="cvf-radar">
          <span className="cvf-ring r1" />
          <span className="cvf-ring r2" />
          <span className="cvf-ring r3" />
          <span className="cvf-sweep" />
          <span className="cvf-core"><Icons.pin /></span>
          <span className="cvf-sat sat1" />
          <span className="cvf-sat sat2" />
        </div>
        <div className="cvf-head">
          <span className="cvf-eyebrow"><span className="cvf-blink" /> Verifica copertura in corso</span>
          <h3>Stiamo interrogando la rete<span className="cvf-dots"><i /><i /><i /></span></h3>
          <p className="cvf-addr"><Icons.pin style={{ width: 14, height: 14 }} /> {target.address}</p>
        </div>
        <ul className="cvf-steps">
          {STEPS.map((s, i) => {
            const state = i < step ? 'done' : i === step ? 'active' : 'wait'
            return (
              <li key={i} className={`cvf-step is-${state}`}>
                <span className="cvf-step-ic">
                  {state === 'done'
                    ? <Icons.check />
                    : state === 'active'
                      ? <span className="cvf-spin" />
                      : <span className="cvf-pend" />}
                </span>
                <span className="cvf-step-txt">
                  <strong>{s.label}</strong>
                  <span>{s.sub}</span>
                </span>
                {state === 'active' && <span className="cvf-pkt" />}
              </li>
            )
          })}
        </ul>
        <div className="cvf-progress">
          <span style={{ width: `${Math.min(100, (step / STEPS.length) * 100)}%` }} />
        </div>
      </div>
    </div>,
    document.body,
  )
}
